import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  KeyValuePairs,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import { InvestigationFieldBreakdown } from "../../lusca-search-api/generated-src";
import { SplitSuggestion, suggestSplits } from "./investigationUtils";
import "./InvestigationPopulationBreakdown.scss";

interface InvestigationPopulationBreakdownProps {
  fieldBreakdown?: InvestigationFieldBreakdown[];
  /** True while the server is answering; keeps the last breakdown on screen. */
  loading?: boolean;
  /** True when a text filter is applied, so the caption can say the numbers follow it. */
  filtered?: boolean;
  /**
   * Called when a value in a tile's legend is clicked, to narrow the items table to exactly that
   * value. Omit to render the legend as plain text.
   */
  onSelectValue?: (selection: BreakdownSelection) => void;
}

/** A clicked breakdown value: the rule field, its display label, and the raw value ("" = none). */
export interface BreakdownSelection {
  field: string;
  label: string;
  value: string;
}

/** Legend rows shown per tile before the rest fold behind "N more values". */
export const VISIBLE_VALUES = 5;

export const NO_VALUE_LABEL = "no value";
export const OTHER_LABEL = "Other";

export const percent = (share: number) => {
  if (share >= 0.995 && share < 1) return ">99%";
  if (share > 0 && share < 0.005) return "<1%";
  return `${Math.round(share * 100)}%`;
};

const formatCount = (n: number) => n.toLocaleString("en-US");

/** Cloudscape's categorical chart palette, so the bars match the Items-per-day chart. */
const PALETTE = [
  tokens.colorChartsPaletteCategorical1,
  tokens.colorChartsPaletteCategorical2,
  tokens.colorChartsPaletteCategorical3,
  tokens.colorChartsPaletteCategorical4,
  tokens.colorChartsPaletteCategorical5,
  tokens.colorChartsPaletteCategorical6,
  tokens.colorChartsPaletteCategorical7,
  tokens.colorChartsPaletteCategorical8,
  tokens.colorChartsPaletteCategorical9,
  tokens.colorChartsPaletteCategorical10,
];
/** Neutral shade for the folded "other values" remainder. */
const OTHER_COLOR = tokens.colorTextBodySecondary;

/**
 * Fields worth a tile (their value varies across the population), most informative first, and
 * fields every item shares, which read better as plain facts than as a one-bar chart.
 */
export function splitBreakdown(
  fieldBreakdown?: InvestigationFieldBreakdown[]
): {
  varying: SplitSuggestion[];
  uniform: SplitSuggestion[];
} {
  const suggestions = suggestSplits(fieldBreakdown);
  return {
    varying: suggestions.filter((s) => s.verdict !== "uniform"),
    uniform: suggestions.filter((s) => s.verdict === "uniform"),
  };
}

interface Segment {
  label: string;
  count: number;
  share: number;
  color: string;
  /** Empty snapshot value or the folded remainder — rendered muted. */
  muted: boolean;
  /** Raw field value; undefined for the folded remainder, which cannot be filtered on. */
  value?: string;
}

export function segmentsFor(suggestion: SplitSuggestion): Segment[] {
  const total = suggestion.totalCount || 1;
  const segments: Segment[] = suggestion.values.map((v, i) => ({
    label: v.value || NO_VALUE_LABEL,
    count: v.count,
    share: v.count / total,
    color: PALETTE[i % PALETTE.length],
    muted: v.value === "",
    value: v.value ?? "",
  }));
  const hidden = suggestion.distinctValues - suggestion.values.length;
  if (hidden > 0 && suggestion.otherCount > 0) {
    segments.push({
      label: `${OTHER_LABEL} (${formatCount(hidden)} more ${
        hidden === 1 ? "value" : "values"
      })`,
      count: suggestion.otherCount,
      share: suggestion.otherCount / total,
      color: OTHER_COLOR,
      muted: true,
    });
  }
  return segments;
}

const StackedBar: React.FC<{ segments: Segment[]; label: string }> = ({
  segments,
  label,
}) => (
  <div
    role="img"
    aria-label={`${label}: ${segments
      .map((s) => `${s.label} ${percent(s.share)}`)
      .join(", ")}`}
    style={{
      display: "flex",
      height: 10,
      borderRadius: 5,
      overflow: "hidden",
      background: tokens.colorBackgroundLayoutMain,
    }}
  >
    {segments.map((s) => (
      <div
        key={s.label}
        title={`${s.label}: ${formatCount(s.count)} (${percent(s.share)})`}
        style={{
          // Keep even <1% slices visible as a sliver.
          flex: `${Math.max(s.share, 0.006)} 0 0`,
          background: s.color,
          opacity: s.muted ? 0.45 : 1,
        }}
      />
    ))}
  </div>
);

const Swatch: React.FC<{ color: string; muted: boolean }> = ({
  color,
  muted,
}) => (
  <span
    aria-hidden
    style={{
      width: 10,
      height: 10,
      borderRadius: 2,
      background: color,
      opacity: muted ? 0.45 : 1,
    }}
  />
);

const Share: React.FC<{ share: number; count: number }> = ({
  share,
  count,
}) => (
  <Box variant="span" textAlign="right">
    <strong>{percent(share)}</strong>{" "}
    <Box variant="span" color="text-body-secondary">
      · {formatCount(count)}
    </Box>
  </Box>
);

const ellipsis: React.CSSProperties = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const FieldTile: React.FC<{
  suggestion: SplitSuggestion;
  onSelectValue?: (selection: BreakdownSelection) => void;
}> = ({ suggestion, onSelectValue }) => {
  const segments = segmentsFor(suggestion);
  const [expanded, setExpanded] = React.useState(false);
  // Fold only when it saves at least two rows; "1 more value" is noisier than the row itself.
  const foldable = segments.length > VISIBLE_VALUES + 1;
  const shown =
    foldable && !expanded ? segments.slice(0, VISIBLE_VALUES) : segments;
  const hidden = segments.slice(shown.length);
  const hiddenCount = hidden.reduce((n, s) => n + s.count, 0);
  const hiddenShare = hidden.reduce((n, s) => n + s.share, 0);
  // Collapsed, `shown` holds only real values (the folded "Other" is always last).
  const hiddenValues = suggestion.distinctValues - shown.length;

  const renderLabel = (s: Segment) => {
    const clickable = !!onSelectValue && s.value !== undefined;
    const text = (
      <span
        title={
          clickable ? `${s.label} · click to show only these items` : s.label
        }
        style={{ ...ellipsis, fontStyle: s.muted ? "italic" : undefined }}
      >
        {s.label}
      </span>
    );
    if (!clickable || !onSelectValue) {
      return (
        <Box variant="span" color={s.muted ? "text-body-secondary" : undefined}>
          {text}
        </Box>
      );
    }
    // A real button (it filters, it does not navigate) that reads like a link.
    return (
      <button
        type="button"
        aria-label={`Show items where ${suggestion.label} is ${s.label}`}
        onClick={() =>
          onSelectValue({
            field: suggestion.field,
            label: suggestion.label,
            value: s.value as string,
          })
        }
        className="breakdown-value"
        style={{
          color: s.muted
            ? tokens.colorTextBodySecondary
            : tokens.colorTextLinkDefault,
        }}
      >
        {text}
      </button>
    );
  };

  return (
    <div data-testid={`breakdown-${suggestion.field}`}>
      <SpaceBetween size="xs">
        <div>
          <Box variant="h4">{suggestion.label}</Box>
          <Box color="text-body-secondary" fontSize="body-s">
            {formatCount(suggestion.distinctValues)} distinct{" "}
            {suggestion.distinctValues === 1 ? "value" : "values"}
          </Box>
        </div>
        <StackedBar segments={segments} label={suggestion.label} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "12px minmax(0, 1fr) auto",
            columnGap: 8,
            rowGap: 4,
            alignItems: "center",
          }}
        >
          {shown.map((s) => (
            <React.Fragment key={s.label}>
              <Swatch color={s.color} muted={s.muted} />
              {renderLabel(s)}
              <Share share={s.share} count={s.count} />
            </React.Fragment>
          ))}
          {foldable && !expanded && (
            <>
              <Swatch color={OTHER_COLOR} muted />
              <span style={ellipsis}>
                <Button
                  variant="inline-link"
                  onClick={() => setExpanded(true)}
                  ariaLabel={`Show ${hiddenValues} more ${suggestion.label} values`}
                >
                  {formatCount(hiddenValues)} more{" "}
                  {hiddenValues === 1 ? "value" : "values"}
                </Button>
              </span>
              <Share share={hiddenShare} count={hiddenCount} />
            </>
          )}
        </div>
        {foldable && expanded && (
          <Button variant="inline-link" onClick={() => setExpanded(false)}>
            Show fewer
          </Button>
        )}
      </SpaceBetween>
    </div>
  );
};

/**
 * What the frozen population is made of: for every rule field the population rule does not pin,
 * how the items are spread across its values — the same read-out the builder shows before an
 * investigation is created, so someone opening a shared link understands the case without
 * reading rows. Counts come from the server over every manifest row matching the current filter.
 *
 * Fields with one value across the population are listed as plain facts ("Shared by every item");
 * fields that vary get a tile with a stacked bar and a legend.
 */
export const InvestigationPopulationBreakdown: React.FC<
  InvestigationPopulationBreakdownProps
> = ({ fieldBreakdown, loading, filtered, onSelectValue }) => {
  const { varying, uniform } = splitBreakdown(fieldBreakdown);
  const total = varying[0]?.totalCount ?? uniform[0]?.totalCount ?? 0;
  const hasData = varying.length > 0 || uniform.length > 0;

  let body: React.ReactNode;
  if (!hasData && loading) {
    body = (
      <Box textAlign="center" color="text-body-secondary">
        <Spinner /> Loading breakdown
      </Box>
    );
  } else if (!hasData) {
    body = (
      <Box textAlign="center" color="inherit">
        <b>No breakdown</b>
        <Box variant="p" color="inherit">
          {filtered
            ? "No items match the current filter."
            : "The population rule pins every field, or the manifest is empty."}
        </Box>
      </Box>
    );
  } else {
    body = (
      <SpaceBetween size="l">
        {uniform.length > 0 && (
          <div data-testid="breakdown-uniform">
            <Box variant="h4" padding={{ bottom: "xs" }}>
              Shared by every item
            </Box>
            <KeyValuePairs
              columns={Math.min(4, uniform.length)}
              items={uniform.map((s) => ({
                label: s.label,
                value: s.values[0]?.value || (
                  <Box variant="span" color="text-body-secondary">
                    <em>{NO_VALUE_LABEL}</em>
                  </Box>
                ),
              }))}
            />
          </div>
        )}
        {varying.length > 0 && (
          <div>
            {uniform.length > 0 && (
              <Box variant="h4" padding={{ bottom: "s" }}>
                Varies between items
              </Box>
            )}
            <ColumnLayout
              columns={Math.min(3, varying.length) as 1 | 2 | 3}
              variant="text-grid"
            >
              {varying.map((s) => (
                <FieldTile
                  key={s.field}
                  suggestion={s}
                  onSelectValue={onSelectValue}
                />
              ))}
            </ColumnLayout>
          </div>
        )}
      </SpaceBetween>
    );
  }

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText="Population breakdown"
      headerCounter={hasData ? `(${formatCount(total)} items)` : undefined}
      headerDescription={
        filtered
          ? "Counts follow the current filter."
          : "What the items have in common, and how they differ."
      }
    >
      {body}
    </ExpandableSection>
  );
};

export default InvestigationPopulationBreakdown;
